import {
  type FilesFileCreateSchema,
  type FilesFileSchemaPartial,
  type FilesFormatSchema,
  type FilesStorageSchema,
  type FilesTranscodeRequestSchema,
  httpGetAssetsByAssetIdFilesByFileId,
  httpGetStoragesByStorageId,
  httpGetStoragesMatchingByPurpose,
  httpPatchAssetsByAssetIdFilesByFileId,
  httpPatchAssetsByAssetIdProxiesByProxyId,
  httpPostAssetsByAssetIdFileSets,
  httpPostAssetsByAssetIdFiles,
  httpPostAssetsByAssetIdFilesByFileIdKeyframes,
  httpPostAssetsByAssetIdFormats,
  httpPostAssetsByAssetIdProxiesByProxyIdKeyframes,
} from '../services/files';
import type { IconikApiSdkMethodOptionsWithParams } from './types';

type CreateFormatParams = {
  assetId: string;
  mimeType: string;
  /**
   * Format name, ORIGINAL by default.
   */
  formatName?: string;
  /**
   * Extra metadata entries, the language of a subtitle format for instance.
   * These cannot be patched into correctness later the way components can, so
   * they have to be sent here.
   */
  metadata?: FilesFormatSchema['metadata'];
  storageMethods?: FilesFormatSchema['storage_methods'];
};

/**
 * `user_id` is deliberately not sent. Web sends it, but every upload this sdk
 * drives creates formats without it, so the API does not require it.
 */
export function createFormat({
  client,
  params: {
    assetId,
    mimeType,
    formatName = 'ORIGINAL',
    metadata = [],
    storageMethods,
  },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CreateFormatParams>) {
  const [callerMetadata = {}, ...furtherMetadata] = metadata;

  return client.request(
    httpPostAssetsByAssetIdFormats(assetId, {
      name: formatName,
      metadata: [
        { internet_media_type: mimeType, ...callerMetadata },
        ...furtherMetadata,
      ],
      storage_methods: storageMethods,
    }),
    { abortSignal },
  );
}

type CreateFileSetParams = {
  assetId: string;
  formatId: string;
  storageId: string;
  directoryPath: string;
  fileName: string;
};

export function createFileSet({
  client,
  params: { assetId, formatId, storageId, directoryPath, fileName },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CreateFileSetParams>) {
  return client.request(
    httpPostAssetsByAssetIdFileSets(assetId, {
      format_id: formatId,
      storage_id: storageId,
      base_dir: directoryPath,
      name: fileName,
      component_ids: [],
    }),
    { abortSignal },
  );
}

type FileType = FilesFileCreateSchema['type'];

type CreateFileParams = {
  assetId: string;
  // Property contentType is required for S3 uploads.
  contentType?: string;
  checksum?: string;
  formatId: string;
  fileSetId: string;
  storageId: string;
  directoryPath: string;
  fileName: string;
  fileSize: number;
  type: FileType;
};

export function createFile({
  client,
  params: {
    assetId,
    contentType,
    checksum,
    formatId,
    fileSetId,
    storageId,
    directoryPath,
    fileName,
    fileSize,
    type = 'FILE',
  },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CreateFileParams>) {
  return client.request(
    httpPostAssetsByAssetIdFiles(assetId, {
      content_type: contentType,
      checksum,
      original_name: fileName,
      directory_path: directoryPath,
      size: fileSize,
      type,
      storage_id: storageId,
      file_set_id: fileSetId,
      format_id: formatId,
    }),
    { abortSignal },
  );
}

type CloseFileParams = {
  assetId: string;
  fileId: string;
};

type GenerateFileKeyframesParams = {
  assetId: string;
  fileId: string;
  transcodeOptions: FilesTranscodeRequestSchema;
};

export function generateFileKeyframes({
  client,
  params: { assetId, fileId, transcodeOptions },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<GenerateFileKeyframesParams>) {
  return client.request(
    httpPostAssetsByAssetIdFilesByFileIdKeyframes(
      assetId,
      fileId,
      transcodeOptions,
    ),
    { abortSignal },
  );
}

export function closeFile({
  client,
  params: { assetId, fileId },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CloseFileParams>) {
  const data: FilesFileSchemaPartial = {
    status: 'CLOSED',
  };

  return client.request(
    httpPatchAssetsByAssetIdFilesByFileId(assetId, fileId, data),
    { abortSignal },
  );
}

type GenerateProxyKeyframesParams = {
  assetId: string;
  proxyId: string;
  transcodeOptions: FilesTranscodeRequestSchema;
};

export function generateProxyKeyframes({
  client,
  params: { assetId, proxyId, transcodeOptions },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<GenerateProxyKeyframesParams>) {
  return client.request(
    httpPostAssetsByAssetIdProxiesByProxyIdKeyframes(
      assetId,
      proxyId,
      transcodeOptions,
    ),
    { abortSignal },
  );
}

type CloseProxyParams = {
  assetId: string;
  proxyId: string;
};

export function closeProxy({
  client,
  params: { assetId, proxyId },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<CloseProxyParams>) {
  return client.request(
    httpPatchAssetsByAssetIdProxiesByProxyId(assetId, proxyId, {
      status: 'CLOSED',
    }),
    { abortSignal },
  );
}

type GetStorageParams = {
  storageId: string;
};

export function getStorage({
  client,
  params: { storageId },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<GetStorageParams>) {
  return client.request(httpGetStoragesByStorageId(storageId), { abortSignal });
}

type GetStorageByMatchingPurposeParams = {
  purpose: FilesStorageSchema['purpose'];
};

export function getStorageByMatchingPurpose({
  client,
  params: { purpose },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<GetStorageByMatchingPurposeParams>) {
  return client.request(httpGetStoragesMatchingByPurpose(purpose), {
    abortSignal,
  });
}

type GetFileParams = {
  assetId: string;
  fileId: string;
  content_type?: string;
};

export function getFile({
  client,
  params: { assetId, fileId },
  abortSignal,
}: IconikApiSdkMethodOptionsWithParams<GetFileParams>) {
  return client.request(httpGetAssetsByAssetIdFilesByFileId(assetId, fileId), {
    abortSignal,
  });
}
